import { ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * The Sign step's CTA sits outside the card, like on Getting Started. But here the
 * button submits forms (ACH and credit card) that live inside the card, and its
 * `disabled` depends on their local state (card validity, loading).
 *
 * Moving the button in the JSX would mean lifting that state. A portal avoids it:
 * the button stays in the same React tree — all the state keeps working — and only
 * the DOM node moves to the slot after the card. The `form` attribute restores the
 * link to the <form>, which the portal breaks by taking the button out of it.
 */
export const CTA_SLOT_ID = 'sign-deal-cta-slot';
export const SIGN_FORM_ID = 'sign-deal-form';

/** The slot element, resolved after mount (the parent renders it in the same pass,
 *  so it already exists by the time this effect runs). */
export const useCtaSlot = () => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setSlot(document.getElementById(CTA_SLOT_ID));
  }, []);

  return slot;
};

/**
 * Puts the CTA in the slot when there is one, and in place when there isn't.
 *
 * The fallback matters: rendering only when the slot resolves means a missing slot
 * leaves no submit button at all, so the user simply cannot sign or pay — with no
 * error. A misplaced button is a much better failure than an absent one.
 */
export const renderCtaInSlot = (cta: ReactNode, slot: HTMLElement | null) =>
  slot ? createPortal(cta, slot) : cta;
